"use client";
import {
  __name
} from "../../../chunk-SHUYVCID.js";

// src/sections/core/contact/contact-cosmic.tsx
import { getErrorFromResult, isOkResult } from "@base/utils/error/result";
import { Icon } from "@core/icons";
import { Button } from "@core/ui/components/atoms/button";
import { Input } from "@core/ui/components/atoms/input";
import { Label } from "@core/ui/components/atoms/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@core/ui/components/atoms/select";
import { Textarea } from "@core/ui/components/atoms/textarea";
import { useCallback, useRef, useState } from "react";
import { isMessageField } from "../../../content/helpers.js";
import { submitFormToEndpoint } from "../../new/form-section/submit-form.js";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var DEFAULT_CONTACT_FIELDS = [
  { id: "name", label: "Name", type: "text", placeholder: "Your name", required: true },
  { id: "email", label: "Email", type: "email", placeholder: "your@email.com", required: true },
  { id: "message", label: "Message", type: "text", placeholder: "How can we help\
?", required: true }
];
var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var PHONE_REGEX = /^[+]?[\d\s()-]{7,20}$/;
function validateField(field, value) {
  const trimmed = value.trim();
  if (field.required === true && trimmed.length === 0) {
    return `${field.label} is required`;
  }
  if (trimmed.length === 0) {
    return null;
  }
  if (field.type === "email" && !EMAIL_REGEX.test(trimmed)) {
    return "Please enter a valid email address";
  }
  if (field.type === "tel" && !PHONE_REGEX.test(trimmed)) {
    return "Please enter a valid phone number";
  }
  if (isMessageField(field.id) && trimmed.length < 10) {
    return "Message must be at least 10 characters";
  }
  return null;
}
__name(validateField, "validateField");
function validateAllFields(fields, values) {
  const errors = {};
  for (const field of fields) {
    const error = validateField(field, values[field.id] ?? "");
    if (error != null) {
      errors[field.id] = error;
    }
  }
  return errors;
}
__name(validateAllFields, "validateAllFields");
function CosmicFieldControl({
  field,
  value,
  showError,
  isMessage,
  onChange,
  onBlur
}) {
  const controlClassName = `border-border/40 bg-muted/50 text-foreground placeho\
lder:text-muted-foreground/60 focus:ring-primary/40 ${showError ? "border-red-50\
0" : ""}`;
  if (field.type === "select" && field.options != null) {
    return /* @__PURE__ */ jsxs(
      Select,
      {
        name: field.id,
        ...field.required !== void 0 && { required: field.required },
        value,
        onValueChange: (v) => onChange(field.id, v),
        children: [
          /* @__PURE__ */ jsx(
            SelectTrigger,
            {
              id: field.id,
              className: `border-border/40 bg-muted/50 text-foreground focus:rin\
g-primary/40 ${showError ? "border-red-500" : ""}`,
              onBlur: () => onBlur(field.id),
              children: /* @__PURE__ */ jsx(SelectValue, { placeholder: field.placeholder ??
              `Select ${field.label}` })
            }
          ),
          /* @__PURE__ */ jsx(SelectContent, { children: field.options.map((option) => /* @__PURE__ */ jsx(
          SelectItem, { value: option, children: option }, option)) })
        ]
      }
    );
  }
  if (isMessage) {
    return /* @__PURE__ */ jsx(
      Textarea,
      {
        id: field.id,
        name: field.id,
        placeholder: field.placeholder,
        required: field.required,
        rows: 4,
        value,
        onChange: (e) => onChange(field.id, e.target.value),
        onBlur: () => onBlur(field.id),
        "aria-invalid": showError,
        "aria-describedby": showError ? `${field.id}-error` : void 0,
        className: controlClassName
      }
    );
  }
  return /* @__PURE__ */ jsx(
    Input,
    {
      id: field.id,
      name: field.id,
      type: field.type,
      placeholder: field.placeholder,
      required: field.required,
      value,
      onChange: (e) => onChange(field.id, e.target.value),
      onBlur: () => onBlur(field.id),
      "aria-invalid": showError,
      "aria-describedby": showError ? `${field.id}-error` : void 0,
      className: controlClassName
    }
  );
}
__name(CosmicFieldControl, "CosmicFieldControl");
function ContactCosmicFormField({
  field,
  value,
  error,
  touched,
  onChange,
  onBlur
}) {
  const isMessage = isMessageField(field.id);
  const showError = touched && error != null;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
    /* @__PURE__ */ jsxs(Label, { htmlFor: field.id, className: "text-sm text-mu\
ted-foreground", children: [
      field.label,
      field.required === true && /* @__PURE__ */ jsx("span", { className: "ml-0.\
5 text-primary", "aria-hidden": "true", children: "*" })
    ] }),
    /* @__PURE__ */ jsx(
      CosmicFieldControl,
      {
        field,
        value,
        showError,
        isMessage,
        onChange,
        onBlur
      }
    ),
    showError && /* @__PURE__ */ jsx("p", { id: `${field.id}-error`, className: "\
text-xs text-red-400", role: "alert", children: error })
  ] });
}
__name(ContactCosmicFormField, "ContactCosmicFormField");
function ContactCosmic({ content, themeId }) {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [values, setValues] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [honeypot, setHoneypot] = useState("");
  const formRef = useRef(null);
  const { heading, description, email, phone, formConfig, responseTime } = content;
  const fields = formConfig?.fields ?? DEFAULT_CONTACT_FIELDS;
  const submitText = formConfig?.submitText ?? "Send Message";
  const successMessage = formConfig?.successMessage ?? "Thank you! We'll be in t\
ouch soon.";
  const handleChange = useCallback(
    (id, value) => {
      setValues((prev) => ({ ...prev, [id]: value }));
      if (touched[id]) {
        const field = fields.find((f) => f.id === id);
        if (field != null) {
          const error = validateField(field, value);
          setErrors((prev) => {
            const next = { ...prev };
            if (error != null) {
              next[id] = error;
            } else {
              delete next[id];
            }
            return next;
          });
        }
      }
    },
    [fields, touched]
  );
  const handleBlur = useCallback(
    (id) => {
      setTouched((prev) => ({ ...prev, [id]: true }));
      const field = fields.find((f) => f.id === id);
      if (field != null) {
        const error = validateField(field, values[id] ?? "");
        setErrors((prev) => {
          const next = { ...prev };
          if (error != null) {
            next[id] = error;
          } else {
            delete next[id];
          }
          return next;
        });
      }
    },
    [fields, values]
  );
  async function handleSubmit(e) {
    e.preventDefault();
    const allTouched = {};
    for (const field of fields) {
      allTouched[field.id] = true;
    }
    setTouched(allTouched);
    const validationErrors = validateAllFields(fields, values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      const firstErrorId = fields.find((f) => validationErrors[f.id] != null)?.id;
      if (firstErrorId != null) {
        document.getElementById(firstErrorId)?.focus();
      }
      return;
    }
    if (formConfig?.formAction == null) {
      setSubmitted(true);
      return;
    }
    const payload = new FormData();
    for (const field of fields) {
      payload.append(field.id, values[field.id] ?? "");
    }
    payload.append("website", honeypot);
    setIsSubmitting(true);
    setSubmitError(null);
    const result = await submitFormToEndpoint(formConfig.formAction, payload);
    setIsSubmitting(false);
    if (isOkResult(result)) {
      setSubmitted(true);
      return;
    }
    setSubmitError(
      getErrorFromResult(result)?.message ?? "We could not send your message. Pl\
ease try again, or email us directly."
    );
  }
  __name(handleSubmit, "handleSubmit");
  if (content == null) {
    return null;
  }
  return /* @__PURE__ */ jsx("section", { className: "px-6 py-16 md:py-24", "dat\
a-theme": themeId, children: /* @__PURE__ */ jsx("div", { className: "mx-auto ma\
x-w-3xl", children: /* @__PURE__ */ jsx("div", { className: "glass-card", children: submitted ?
  /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-4 py-\
12 text-center", children: [
    /* @__PURE__ */ jsx("div", { className: "flex h-16 w-16 items-center justify\
-center rounded-full bg-primary/10", children: /* @__PURE__ */ jsx(Icon, { icon: "\
Check", className: "size-8 text-primary" }) }),
    /* @__PURE__ */ jsx("p", { className: "text-lg font-semibold text-foreground",
    children: successMessage })
  ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-8", children: [
      /* @__PURE__ */ jsx("h2", { className: "text-2xl font-bold text-foreground\
 md:text-3xl", children: heading }),
      description != null && /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm\
 text-muted-foreground", children: description })
    ] }),
    (email != null || phone != null || responseTime != null) && /* @__PURE__ */ jsxs(
    "div", { className: "mb-8 flex flex-wrap gap-3", children: [
      email != null && /* @__PURE__ */ jsxs(
        "a",
        {
          href: `mailto:${email}`,
          className: "glass-pill text-sm text-muted-foreground transition-colors\
 hover:text-primary",
          children: [
            /* @__PURE__ */ jsx(Icon, { icon: "Mail", className: "size-3.5" }),
            email
          ]
        }
      ),
      phone != null && /* @__PURE__ */ jsxs(
        "a",
        {
          href: `tel:${phone}`,
          className: "glass-pill text-sm text-muted-foreground transition-colors\
 hover:text-primary",
          children: [
            /* @__PURE__ */ jsx(Icon, { icon: "Phone", className: "size-3.5" }),
            phone
          ]
        }
      ),
      responseTime != null && /* @__PURE__ */ jsxs("span", { className: "glass-p\
ill text-xs text-muted-foreground", children: [
        /* @__PURE__ */ jsx(Icon, { icon: "Clock", className: "size-3.5" }),
        "Responds ",
        responseTime
      ] })
    ] }),
    /* @__PURE__ */ jsxs(
      "form",
      {
        ref: formRef,
        onSubmit: handleSubmit,
        className: "flex flex-col gap-5",
        noValidate: true,
        children: [
          fields.map((field) => /* @__PURE__ */ jsx(
            ContactCosmicFormField,
            {
              field,
              value: values[field.id] ?? "",
              error: errors[field.id],
              touched: touched[field.id] ?? false,
              onChange: handleChange,
              onBlur: handleBlur
            },
            field.id
          )),
          /* @__PURE__ */ jsx(
            Input,
            {
              type: "text",
              name: "website",
              tabIndex: -1,
              autoComplete: "off",
              "aria-hidden": "true",
              className: "hidden",
              value: honeypot,
              onChange: (e) => setHoneypot(e.target.value)
            }
          ),
          submitError != null && /* @__PURE__ */ jsxs(
            "div",
            {
              role: "alert",
              className: "flex items-start gap-2 rounded-lg border border-destru\
ctive/40 bg-destructive/10 p-3",
              children: [
                /* @__PURE__ */ jsx(
                  Icon,
                  {
                    icon: "TriangleAlert",
                    className: "mt-0.5 size-4 shrink-0 text-destructive"
                  }
                ),
                /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive",
                children: submitError })
              ]
            }
          ),
          /* @__PURE__ */ jsx(Button, { type: "submit", size: "lg", className: "\
mt-2 w-full", disabled: isSubmitting, children: isSubmitting ? /* @__PURE__ */ jsxs(
          Fragment, { children: [
            /* @__PURE__ */ jsx(Icon, { icon: "Loader2", className: "mr-2 size-4\
 animate-spin" }),
            "Sending..."
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            submitText,
            /* @__PURE__ */ jsx(Icon, { icon: "ArrowRight", className: "ml-2 siz\
e-4" })
          ] }) })
        ]
      }
    )
  ] }) }) }) });
}
__name(ContactCosmic, "ContactCosmic");
export {
  ContactCosmic
};
